import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Select, DatePicker, InputNumber, Space, message, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { companyService } from '../../services/companyService';
import api from '../../services/api';

const { Option } = Select;

const EditCompany = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [packages, setPackages] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch company and packages in parallel
      const [companyRes, packagesRes] = await Promise.all([
        companyService.getCompany(id),
        api.get('/packages')
      ]);

      if (companyRes.success) {
        const company = companyRes.data;
        form.setFieldsValue({
          companyName: company.companyName,
          clientName: company.clientName,
          phone: company.phone,
          email: company.email,
          packageId: company.packageId,
          startDate: moment(company.startDate),
          expiryDate: moment(company.expiryDate),
          discount: parseFloat(company.discount) || 0,
          finalPrice: parseFloat(company.finalPrice) || 0,
          status: company.status,
          notes: company.notes
        });
      } else {
        message.error('Company not found');
        navigate('/companies');
      }

      if (packagesRes.data.success) {
        setPackages(packagesRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      const updateData = {
        ...values,
        startDate: values.startDate.format('YYYY-MM-DD'),
        expiryDate: values.expiryDate.format('YYYY-MM-DD')
      };

      const response = await companyService.updateCompany(id, updateData);

      if (response.success) {
        message.success('Company updated successfully');
        navigate('/companies');
      } else {
        message.error(response.message || 'Failed to update company');
      }
    } catch (error) {
      console.error('Error updating company:', error);
      message.error('Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" tip="Loading company..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Edit Company" style={{ maxWidth: 800, margin: '0 auto' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item label="Company Name" name="companyName" rules={[{ required: true }]}>
            <Input placeholder="Company name" />
          </Form.Item>

          <Form.Item label="Client Name" name="clientName" rules={[{ required: true }]}>
            <Input placeholder="Client name" />
          </Form.Item>

          <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="Email" />
          </Form.Item>

          <Form.Item label="Phone" name="phone" rules={[{ required: true }]}>
            <Input placeholder="Phone number" maxLength={10} />
          </Form.Item>

          <Form.Item label="Package" name="packageId" rules={[{ required: true }]}>
            <Select placeholder="Select package">
              {packages.map(pkg => (
                <Option key={pkg.id} value={pkg.id}>
                  {pkg.name} - {pkg.userLimit} Users - ₹{parseFloat(pkg.price).toLocaleString()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Start Date" name="startDate" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Expiry Date" name="expiryDate" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Discount (%)" name="discount">
            <InputNumber min={0} max={50} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Final Price (₹)" name="finalPrice" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="Status" name="status" rules={[{ required: true }]}>
            <Select>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="expired">Expired</Option>
              <Option value="suspended">Suspended</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} placeholder="Additional notes" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving} size="large">
                Update Company
              </Button>
              <Button onClick={() => navigate('/companies')} size="large">
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default EditCompany;
